export type TTSText = {
  id?: string;
  text: string;
  lang?: string; // BCP-47
  rate?: number; // 0.1..10 (Web Speech); we clamp
  pitch?: number; // 0..2
  volume?: number; // 0..1
};

export interface TTSEngine {
  speak(input: TTSText): Promise<void>;
  stop(): void;
  isSupported(): boolean;
  name: string;
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

// -------------------- Web Speech API Engine --------------------
class WebSpeechEngine implements TTSEngine {
  name = "webspeech";

  private pickVoice(lang?: string): SpeechSynthesisVoice | null {
    try {
      const synth = window.speechSynthesis;
      if (!synth) return null;
      const voices = synth.getVoices();
      if (!voices || !voices.length) return null;
      const base = (lang || "").split("-")[0].toLowerCase();
      // Prefer exact match, then base-language match
      const exact = voices.find(
        (v) => v.lang?.toLowerCase() === (lang || "").toLowerCase()
      );
      if (exact) return exact;
      if (base) {
        const baseMatch = voices.find((v) =>
          v.lang?.toLowerCase().startsWith(base)
        );
        if (baseMatch) return baseMatch;
      }
      return voices[0] || null;
    } catch {
      return null;
    }
  }

  speak(input: TTSText): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        const synth = window.speechSynthesis;
        if (!synth) return reject(new Error("speechSynthesis not available"));
        const u = new SpeechSynthesisUtterance(input.text || "");
        const v = this.pickVoice(input.lang);
        if (v) u.voice = v;
        if (input.lang) u.lang = input.lang;
        u.rate = clamp(input.rate ?? 1, 0.5, 2);
        u.pitch = clamp(input.pitch ?? 1, 0, 2);
        u.volume = clamp(input.volume ?? 1, 0, 1);
        u.onend = () => resolve();
        u.onerror = (e) => reject(new Error((e as any)?.error || "tts error"));
        synth.speak(u);
      } catch (e: any) {
        reject(e);
      }
    });
  }

  stop(): void {
    try {
      const synth = window.speechSynthesis;
      synth?.cancel();
    } catch {}
  }

  isSupported(): boolean {
    return typeof window !== "undefined" && !!window.speechSynthesis;
  }
}

// -------------------- Azure TTS Engine (client-proxied stub) --------------------
// This engine calls a server-side proxy that returns an audio/mpeg payload to avoid exposing secrets.
// Configure proxy URL via VITE_FUNC_BASE/api/azureTTS (to be implemented server-side).
class AzureTTSEngine implements TTSEngine {
  name = "azure";
  private audio: HTMLAudioElement | null = null;

  async speak(input: TTSText): Promise<void> {
    const base = (import.meta as any).env?.VITE_FUNC_BASE as string | undefined;
    const url = base ? `${base}/api/azureTTS` : undefined;
    if (!url) throw new Error("Azure TTS proxy not configured");

    const body = {
      text: input.text,
      lang: input.lang,
      rate: input.rate,
      pitch: input.pitch,
      volume: input.volume,
    } as Record<string, unknown>;

    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error(`Azure TTS failed: ${res.statusText}`);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    try {
      await this.play(objUrl, clamp(input.volume ?? 1, 0, 1));
    } finally {
      URL.revokeObjectURL(objUrl);
    }
  }

  private play(src: string, volume = 1): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        if (this.audio) {
          try {
            this.audio.pause();
          } catch {}
        }
        const a = new Audio();
        a.src = src;
        a.volume = volume;
        a.onended = () => resolve();
        a.onerror = () => reject(new Error("audio playback error"));
        this.audio = a;
        const p = a.play();
        if (p && typeof p.then === "function") p.catch((e) => reject(e));
      } catch (e) {
        reject(e as any);
      }
    });
  }

  stop(): void {
    try {
      this.audio?.pause();
    } catch {}
    this.audio = null;
  }

  isSupported(): boolean {
    // Requires server proxy; assume supported if browser can play audio
    return typeof Audio !== "undefined";
  }
}

export type TTSProvider = "webspeech" | "azure";

export function createTTSEngine(
  preferred: TTSProvider = "webspeech"
): TTSEngine {
  if (preferred === "azure") {
    return new AzureTTSEngine();
  }
  return new WebSpeechEngine();
}
